import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store/reducer';
import {
  includes6Characters,
  includesLowercaseLetter,
  includesNumber,
  includesUppercaseLetter,
  passwordsMatch,
} from 'utils/validation';
import messages from '../../app/_authenticated/settings/update-password/components/messages';
import { TextWithCheckbox } from '../TextWithCheckbox/TextWithCheckbox';

interface PassswordRequirementsResponse {
  has6Characters: boolean;
  hasUppercaseLetter: boolean;
  hasLowercaseLetter: boolean;
  hasNumber: boolean;
  hasPasswordMatch: boolean;
}

const initialState: PassswordRequirementsResponse = {
  has6Characters: false,
  hasUppercaseLetter: false,
  hasLowercaseLetter: false,
  hasNumber: false,
  hasPasswordMatch: false,
};

export const PasswordRequirementsAndMatch = ({
  passwordState,
  isPasswordMatch,
  passwordValidationChanged,
}: {
  passwordState: any;
  isPasswordMatch?: boolean;
  passwordValidationChanged?: (value: boolean) => void;
}) => {
  const defaultNewPasswordValue = '';
  const newPassword =
    typeof passwordState?.newPassword === 'undefined'
      ? defaultNewPasswordValue
      : passwordState?.newPassword;
  const confirmNewPassword = passwordState?.confirmNewPassword;

  const [status, setStatus] =
    useState<PassswordRequirementsResponse>(initialState);

  const isValid = (requirements: PassswordRequirementsResponse) => {
    return (
      requirements.has6Characters &&
      requirements.hasLowercaseLetter &&
      requirements.hasNumber &&
      requirements.hasUppercaseLetter &&
      (!isPasswordMatch || requirements.hasPasswordMatch)
    );
  };
  useEffect(() => {
    const requirements: PassswordRequirementsResponse = {
      has6Characters: includes6Characters(newPassword),
      hasUppercaseLetter: includesUppercaseLetter(newPassword),
      hasLowercaseLetter: includesLowercaseLetter(newPassword),
      hasNumber: includesNumber(newPassword),
      hasPasswordMatch: passwordsMatch(newPassword, confirmNewPassword),
    };
    setStatus(requirements);
    passwordValidationChanged?.(isValid(requirements));
  }, [newPassword, confirmNewPassword]);

  const { language } = useSelector((state: RootState) => state.language);
  const { passwordRequirements } = messages[language];

  return (
    <div className="mb-4 mt-4 pb-4 pt-4">
      <div className="mb-4 items-content">{passwordRequirements.heading}</div>
      <div className="flex flex-row mb-3">
        <div className="flex flex-row w-1/2 items-center">
          <TextWithCheckbox
            className="ml-6 absolute"
            isValid={status?.hasLowercaseLetter}
            requirementMessage={passwordRequirements.lowercaseLetter}
          />
        </div>
        <div className="flex flex-row w-1/2 items-center">
          <TextWithCheckbox
            className="ml-2"
            isValid={status?.hasUppercaseLetter}
            requirementMessage={passwordRequirements.uppercaseLetter}
          />
        </div>
      </div>
      <div className="flex flex-row mb-3">
        <div className="flex flex-row w-1/2 items-center">
          <TextWithCheckbox
            className="ml-6 absolute"
            isValid={status?.has6Characters}
            requirementMessage={passwordRequirements.character}
          />
        </div>
        <div className="flex flex-row w-1/2 items-center">
          <TextWithCheckbox
            className="ml-2"
            isValid={status?.hasNumber}
            requirementMessage={passwordRequirements.number}
          />
        </div>
      </div>
      {isPasswordMatch && (
        <div className="flex flex-row mb-3">
          <div className="flex flex-row w-1/2 items-center">
            <TextWithCheckbox
              className="ml-6"
              isValid={status?.hasPasswordMatch}
              requirementMessage={passwordRequirements.passwordsMatch}
            />
          </div>
        </div>
      )}
    </div>
  );
};
